// Aurora Innovation, Inc. Proprietary and Confidential. Copyright 2026.
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { badgeClasses } from '@mui/material/Badge';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { rem } from '../../tokens/conversions';
import { typography } from '../../tokens/typography';
import { badgeColors } from './badgeColors';
var badgeSlot = ".".concat(badgeClasses.badge);
var badge = "& ".concat(badgeSlot);
var icon = "& .".concat(svgIconClasses.root);
// Figma offers the dot treatments as two independent booleans (Outline Dot, Bordered Dot). They
// cannot be props: MUI forwards any prop it doesn't consume to the root <span>, and React warns
// about camelCase attributes it doesn't recognise. A data attribute forwards cleanly instead.
var treatment = function(value) {
    return '&[data-dot-style="'.concat(value, '"] ').concat(badgeSlot);
};
// Dots are filled from this variable rather than from the color class's background, because
// `color="default"` fills with its *border* token in Figma — its level-200 background would be
// almost invisible at 8px. Every color class sets the variable; the `badge` slot holds the
// `default` value, since MUI generates no `colorDefault` class.
var DOT_COLOR_VAR = '--ads-badge-dot-color';
var dotColor = "var(".concat(DOT_COLOR_VAR, ")");
var PILL_RADIUS = 64;
var DOT_DIAMETER = {
    small: 8,
    medium: 12,
    large: 16
};
var RING_WIDTH = {
    small: 1,
    medium: 2,
    large: 2
};
var ICON_SIZE = {
    small: 12,
    medium: 16,
    large: 20
};
var STANDARD = {
    small: _object_spread_props(_object_spread({}, typography.actionTiny), {
        height: rem(16),
        minWidth: rem(16),
        padding: "0 ".concat(rem(1))
    }),
    medium: _object_spread_props(_object_spread({}, typography.action1), {
        height: rem(22),
        minWidth: rem(22),
        padding: rem(3)
    }),
    large: _object_spread_props(_object_spread({}, typography.action1), {
        height: rem(28),
        minWidth: rem(28),
        padding: "".concat(rem(3), " ").concat(rem(3), " ").concat(rem(3), " ").concat(rem(4))
    })
};
var ICON_LABEL = {
    small: _object_spread_props(_object_spread({}, typography.actionTiny), {
        height: rem(16),
        minWidth: rem(18),
        padding: "".concat(rem(2), " ").concat(rem(4), " ").concat(rem(2), " ").concat(rem(2)),
        gap: rem(2)
    }),
    medium: _object_spread_props(_object_spread({}, typography.action1), {
        height: rem(22),
        minWidth: rem(22),
        padding: "".concat(rem(3), " ").concat(rem(5), " ").concat(rem(3), " ").concat(rem(3)),
        gap: rem(4)
    }),
    large: _object_spread_props(_object_spread({}, typography.action1), {
        height: rem(28),
        minWidth: rem(28),
        padding: "".concat(rem(3), " ").concat(rem(8), " ").concat(rem(3), " ").concat(rem(4)),
        gap: rem(4)
    })
};
var ICON_ONLY = {
    small: {
        height: rem(16),
        minWidth: rem(16),
        padding: "0 ".concat(rem(2))
    },
    medium: {
        height: rem(22),
        minWidth: rem(22),
        padding: rem(3)
    },
    large: {
        height: rem(28),
        minWidth: rem(28),
        padding: rem(4)
    }
};
var withIcon = function(size, style) {
    return _object_spread_props(_object_spread({}, style), _define_property({}, icon, {
        fontSize: rem(ICON_SIZE[size])
    }));
};
var dot = function(size, ring) {
    var diameter = rem(DOT_DIAMETER[size]);
    var ringWidth = rem(RING_WIDTH[size]);
    var _obj;
    return _obj = {}, _define_property(_obj, badge, {
        width: diameter,
        height: diameter,
        minWidth: diameter,
        padding: 0,
        border: 'none',
        backgroundColor: dotColor
    }), _define_property(_obj, treatment('outline'), {
        backgroundColor: 'transparent',
        border: "".concat(rem(1), " solid ").concat(dotColor)
    }), _define_property(_obj, treatment('bordered'), {
        border: "".concat(ringWidth, " solid ").concat(ring)
    }), // Figma nests a 1px ring in the badge color inside the level-0 ring. An inset shadow paints
    // exactly that without a pseudo-element, because inset shadows are clipped to the padding
    // box — which is where the nested circle's outer edge sits.
    _define_property(_obj, treatment('outline-bordered'), {
        backgroundColor: 'transparent',
        border: "".concat(ringWidth, " solid ").concat(ring),
        boxShadow: "inset 0 0 0 ".concat(rem(1), " ").concat(dotColor)
    }), _obj;
};
export var Badge = function(mode) {
    var c = badgeColors(mode);
    var _obj, _obj1, _obj2, _obj3, _obj4, _obj5, _obj6;
    var override = {
        MuiBadge: {
            styleOverrides: {
                // MUI's Badge never adds a `colorDefault` class — its class-generation logic
                // special-cases the literal `color: 'default'` as "no color class at all"
                // (see @mui/material/Badge/Badge.js). The `default` look must live on the
                // root `badge` slot as a fallback instead; colorInfo/colorError/etc below
                // still win for explicit colors since they're inserted after this in the
                // stylesheet (equal specificity, source order decides).
                badge: (_obj = {
                    color: c.default.text,
                    backgroundColor: c.default.background,
                    border: "0px solid ".concat(c.default.border),
                    borderRadius: rem(PILL_RADIUS),
                    // Badge content is a short count or label; Figma never wraps it.
                    flexWrap: 'nowrap',
                    whiteSpace: 'nowrap'
                }, _define_property(_obj, DOT_COLOR_VAR, c.default.border), _define_property(_obj, icon, {
                    color: c.default.icon
                }), _obj),
                colorInfo: (_obj1 = {
                    color: c.info.text,
                    backgroundColor: c.info.background,
                    border: "0px solid ".concat(c.info.border)
                }, _define_property(_obj1, DOT_COLOR_VAR, c.info.background), _define_property(_obj1, icon, {
                    color: c.info.icon
                }), _obj1),
                colorError: (_obj2 = {
                    color: c.error.text,
                    backgroundColor: c.error.background,
                    border: "0px solid ".concat(c.error.border)
                }, _define_property(_obj2, DOT_COLOR_VAR, c.error.background), _define_property(_obj2, icon, {
                    color: c.error.icon
                }), _obj2),
                colorWarning: (_obj3 = {
                    color: c.warning.text,
                    backgroundColor: c.warning.background,
                    border: "0px solid ".concat(c.warning.border)
                }, _define_property(_obj3, DOT_COLOR_VAR, c.warning.background), _define_property(_obj3, icon, {
                    color: c.warning.icon
                }), _obj3),
                colorSuccess: (_obj4 = {
                    color: c.success.text,
                    backgroundColor: c.success.background,
                    border: "0px solid ".concat(c.success.border)
                }, _define_property(_obj4, DOT_COLOR_VAR, c.success.background), _define_property(_obj4, icon, {
                    color: c.success.icon
                }), _obj4),
                colorSpecial: (_obj5 = {
                    color: c.special.text,
                    backgroundColor: c.special.background,
                    border: "0px solid ".concat(c.special.border)
                }, _define_property(_obj5, DOT_COLOR_VAR, c.special.background), _define_property(_obj5, icon, {
                    color: c.special.icon
                }), _obj5),
                colorDefaultBold: (_obj6 = {
                    color: c.defaultBold.text,
                    backgroundColor: c.defaultBold.background,
                    border: "0px solid ".concat(c.defaultBold.border)
                }, _define_property(_obj6, DOT_COLOR_VAR, c.defaultBold.background), _define_property(_obj6, icon, {
                    color: c.defaultBold.icon
                }), _obj6)
            },
            defaultProps: {
                color: 'info',
                variant: 'standard',
                size: 'medium'
            },
            variants: [
                {
                    props: {
                        variant: 'standard',
                        size: 'small'
                    },
                    style: _define_property({}, badge, STANDARD.small)
                },
                {
                    props: {
                        variant: 'standard',
                        size: 'medium'
                    },
                    style: _define_property({}, badge, STANDARD.medium)
                },
                {
                    props: {
                        variant: 'standard',
                        size: 'large'
                    },
                    style: _define_property({}, badge, STANDARD.large)
                },
                {
                    props: {
                        variant: 'dot',
                        size: 'small'
                    },
                    style: dot('small', c.ring)
                },
                {
                    props: {
                        variant: 'dot',
                        size: 'medium'
                    },
                    style: dot('medium', c.ring)
                },
                {
                    props: {
                        variant: 'dot',
                        size: 'large'
                    },
                    style: dot('large', c.ring)
                },
                {
                    props: {
                        variant: 'iconLabel',
                        size: 'small'
                    },
                    style: _define_property({}, badge, withIcon('small', ICON_LABEL.small))
                },
                {
                    props: {
                        variant: 'iconLabel',
                        size: 'medium'
                    },
                    style: _define_property({}, badge, withIcon('medium', ICON_LABEL.medium))
                },
                {
                    props: {
                        variant: 'iconLabel',
                        size: 'large'
                    },
                    style: _define_property({}, badge, withIcon('large', ICON_LABEL.large))
                },
                {
                    props: {
                        variant: 'iconOnly',
                        size: 'small'
                    },
                    style: _define_property({}, badge, withIcon('small', ICON_ONLY.small))
                },
                {
                    props: {
                        variant: 'iconOnly',
                        size: 'medium'
                    },
                    style: _define_property({}, badge, withIcon('medium', ICON_ONLY.medium))
                },
                {
                    props: {
                        variant: 'iconOnly',
                        size: 'large'
                    },
                    style: _define_property({}, badge, withIcon('large', ICON_ONLY.large))
                }
            ]
        }
    };
    return override;
};

//# sourceMappingURL=Badge.js.map